import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { AppDispatch, RootState } from "app/store";
import { USER_RESTRICTED_CODE, UserRestrictionFeature } from "app/types";
import { useUserRestriction, restrictionUntilText } from "app/restriction";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay } from "common/utils";
import { AccountType } from "features/user/types";
import { PieceStatus } from "features/builder/types";
import { usePieceListScroll } from "features/builder/hook";
import { actions, createNewPiece } from "features/builder/slice";
import { TOP_GUIDE_CHAPTERS } from "features/builder/guideBookPages";
import {
  getMyPieces,
  trashPiece,
  publishPiece,
  unpublishPiece,
  copyPiece,
  createDraft,
  updatePieceMeta,
} from "features/builder/api";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PieceLayoutMode,
  PublishedFilter,
  comparePieces,
  toCardPiece,
} from "features/builder/pieceList";
import {
  GuideBookId,
  BuilderGuideBook,
  MapCard,
  MapListItem,
  AddMapButton,
  AddMapCard,
  BuilderTutorialCard,
  CreateMapModal,
  MapDetailPage,
  BuilderConfirmDialog,
  PiecePublishOptions,
  BuilderAccountRequiredDialog,
  BuilderInfoDialog,
  BuilderModalAccent,
  MyMapsHeader,
  BuilderSidebar,
  SidebarSection,
  LayoutModeMenu,
  LAYOUT_GRID_CLASS,
} from "features/builder/elements";
import { GuideTargetId } from "./PieceBuilderTopGuideOverlay";

/** 未登録（ゲスト）のあいだに作れるピースの数。登録すれば制限しない */
const GUEST_PIECE_LIMIT = 1;

export const MyMapsTab = ({
  onOpenImage,
  onDetailChange,
}: {
  onOpenImage: (src: string, alt: string) => void;
  onDetailChange?: (open: boolean) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const active = useSelector((state: RootState) => state.user.active);
  // 保護者設定。OFF のときは公開できないので、押した時点で伝える
  const piecePublishAllowed = useSelector(
    (state: RootState) => state.config.userConfig.piece_publish,
  );
  // 運営が公開を止めている。保護者の許可を変えても直らないので、別の案内を出す
  const restriction = useUserRestriction(UserRestrictionFeature.PIECE_PUBLISH);
  const [pieces, setPieces] = useState<MockPiece[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [showCreateDenied, setShowCreateDenied] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { sortAxis, sortOrder, filter, layoutMode } = useSelector(
    (state: RootState) => state.builder.listUi.mine,
  );
  const [trashTarget, setTrashTarget] = useState<MockPiece | null>(null);
  const [copyTarget, setCopyTarget] = useState<MockPiece | null>(null);
  const [hintTarget, setHintTarget] = useState<MockPiece | null>(null);
  const [publishTarget, setPublishTarget] = useState<MockPiece | null>(null);
  const [showPublishDenied, setShowPublishDenied] = useState(false);
  const [showPublishRestricted, setShowPublishRestricted] = useState(false);
  // 公開するときに作者が決める、このピースの許可
  const [publishOptions, setPublishOptions] = useState({ copyable: true, commentable: true });
  const [addCardVisible, setAddCardVisible] = useState(true);
  const {
    ref: scrollCallbackRef,
    onScroll,
    scrollRef,
  } = usePieceListScroll(PieceListContext.MINE, pieces.length > 0);
  const addCardRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!active?.uid) return;
    try {
      const items = await getMyPieces(active.uid);
      setPieces(items.map(toCardPiece));
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_LOAD_FAILED")}</p>
        </div>,
      );
    }
  }, [active?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleMaps = useMemo(() => {
    let list = [...pieces];
    list = list.filter(
      (m) =>
        (filter.published === PublishedFilter.ALL ||
          (filter.published === PublishedFilter.PUBLISHED) === m.published) &&
        (!filter.incomplete || !m.completed) &&
        (!filter.editing || m.status === PieceStatus.EDITING) &&
        (!filter.favorite || m.liked > 0),
    );
    list.sort(comparePieces(sortAxis, sortOrder));
    return list;
  }, [pieces, sortAxis, sortOrder, filter]);

  const selected = pieces.find((m) => m.piece_id === selectedId) ?? null;

  // メッセージの「ピースを みる」から来たときは、そのピースの詳細を開く
  const pendingDetail = useSelector((state: RootState) => state.builder.pendingDetail);
  const [initialTab, setInitialTab] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!pendingDetail) return;
    if (!pieces.some((m) => m.piece_id === pendingDetail.pieceId)) return;
    setInitialTab(pendingDetail.tab);
    setSelectedId(pendingDetail.pieceId);
    dispatch(actions.clearPieceDetailRequest());
  }, [pendingDetail, pieces, dispatch]);

  useEffect(() => {
    onDetailChange?.(selectedId !== null);
  }, [selectedId, onDetailChange]);

  // 追加カードが一覧内に見えている間は右下FABを隠し、画面外に出たら表示する
  useEffect(() => {
    const target = addCardRef.current;
    const root = scrollRef.current;
    if (!target || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setAddCardVisible(entry.isIntersecting),
      { root, threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [layoutMode, visibleMaps.length, scrollRef]);

  // 未登録のうちは1つだけ。2つ目を押した時点で理由を出す（作らせてから断らない）
  const isGuest = active?.account_type === AccountType.GUEST;
  const handleOpenCreate = () => {
    if (isGuest && pieces.length >= GUEST_PIECE_LIMIT) {
      setShowCreateDenied(true);
      return;
    }
    setCreateOpen(true);
  };

  const handleCreate = (name: string) => {
    if (!active?.uid) return;
    btnDelay(async () => {
      setCreateOpen(false);
      try {
        const created = await dispatch(createNewPiece({ uid: active.uid, name })).unwrap();
        navigate(`/courses/builder/${created.piece_id}`);
      } catch {
        toast.warn(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
          </div>,
        );
      }
    });
  };

  const handleAction = (piece: MockPiece) => (action: PieceCardAction) => {
    switch (action) {
      case PieceCardAction.DETAIL:
        setSelectedId(piece.piece_id);
        break;
      case PieceCardAction.EDIT:
        if (!active?.uid) break;
        btnDelay(async () => {
          try {
            // 完成済みは draft を作ってから（正本を凍結したまま編集）
            if (piece.completed) {
              await createDraft({ piece_id: piece.piece_id, uid: active.uid });
            }
            // 読み込みは編集画面側のローダー（URL の piece_id）が行う
            navigate(`/courses/builder/${piece.piece_id}`);
          } catch {
            toast.warn(
              <div>
                <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
              </div>,
            );
          }
        });
        break;
      case PieceCardAction.PLAY:
        if (!active?.uid) break;
        btnDelay(() => {
          navigate(`/courses/builder/${piece.piece_id}/preview?from=mine`);
        });
        break;
      case PieceCardAction.COPY:
        setCopyTarget(piece);
        break;
      case PieceCardAction.HINT:
        setHintTarget(piece);
        break;
      case PieceCardAction.TO_TRASH:
        setTrashTarget(piece);
        break;
      case PieceCardAction.TOGGLE_PUBLISH:
        // 公開できるかは手元の設定で分かる。API を投げて 403 を見るまでもなく、
        // 押した時点で理由を出す（運営が止めている / 未登録 / 保護者が公開を許可していない）
        if (!piece.published && active?.account_type !== AccountType.GUEST && restriction) {
          setShowPublishRestricted(true);
          break;
        }
        if (
          !piece.published &&
          (active?.account_type === AccountType.GUEST || !piecePublishAllowed)
        ) {
          setShowPublishDenied(true);
          break;
        }
        setPublishOptions({ copyable: piece.enable_copied, commentable: piece.enable_comment });
        setPublishTarget(piece);
        break;
      default:
        break;
    }
  };

  const handleConfirmTrash = async () => {
    if (!trashTarget || !active?.uid) return;
    const target = trashTarget;
    setTrashTarget(null);
    try {
      await trashPiece({ piece_id: target.piece_id, uid: active.uid });
      if (selectedId === target.piece_id) setSelectedId(null);
      await load();
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_TO_TRASH_TOAST")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  const handleConfirmCopy = async () => {
    if (!copyTarget || !active?.uid) return;
    const target = copyTarget;
    setCopyTarget(null);
    try {
      await copyPiece({ piece_id: target.piece_id, uid: active.uid });
      await load();
      toast.success(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_COPY_TOAST")}</p>
        </div>,
      );
    } catch {
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  const handleConfirmPublish = async () => {
    if (!publishTarget || !active?.uid) return;
    const target = publishTarget;
    const next = !target.published;
    setPublishTarget(null);
    try {
      if (next) {
        // 公開と同時に、このピースの許可（コピー/コメント）も保存する
        await updatePieceMeta({
          piece_id: target.piece_id,
          uid: active.uid,
          enable_copied: publishOptions.copyable,
          enable_comment: publishOptions.commentable,
        });
        await publishPiece({ piece_id: target.piece_id, uid: active.uid });
      } else {
        await unpublishPiece({ piece_id: target.piece_id, uid: active.uid });
      }
      await load();
      toast.success(
        <div>
          <p className="text text-textcolor-black">
            {I18n.t(
              next ? "MSG_BUILDER_TOGGLE_PUBLISH_TOAST" : "MSG_BUILDER_TOGGLE_UNPUBLISH_TOAST",
            )}
          </p>
        </div>,
      );
    } catch (error) {
      // 保護者が「ピースの こうかい」を許可していないと 403。
      // 「しっぱいしました」では理由が分からないので、編集画面と同じ案内を出す
      // 手元の設定と食い違ったとき（保護者が直後に変更した等）の保険
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        // 運営が止めている（手元の設定を読んだあとにかけられた等）
        if (error.response.data?.code === USER_RESTRICTED_CODE) {
          setShowPublishRestricted(true);
          return;
        }
        setShowPublishDenied(true);
        return;
      }
      toast.warn(
        <div>
          <p className="text text-textcolor-black">{I18n.t("MSG_BUILDER_ACTION_FAILED")}</p>
        </div>,
      );
    }
  };

  return (
    <>
      {selected ? (
        <MapDetailPage
          initialTab={initialTab}
          piece={selected}
          context={PieceListContext.MINE}
          onBack={() => setSelectedId(null)}
          onAction={handleAction(selected)}
          onOpenImage={onOpenImage}
        />
      ) : (
        <div className="flex-row-el relative w-full h-full">
          <BuilderSidebar>
            <MyMapsHeader
              count={visibleMaps.length}
              sortAxis={sortAxis}
              sortOrder={sortOrder}
              filter={filter}
              onChangeSort={(axis, order) =>
                dispatch(actions.setListSort({ context: PieceListContext.MINE, axis, order }))
              }
              onChangeFilter={(next) => dispatch(actions.setMineFilter(next))}
            />
            <SidebarSection
              icon="grid_view"
              labelKey="MSG_BUILDER_SECTION_LAYOUT"
              accentClass="text-green-100"
              divider={false}
            >
              <LayoutModeMenu
                value={layoutMode}
                accentTextClass="text-green-100"
                accentBgClass="bg-green-100"
                accentBorderClass="border-green-100"
                onChange={(mode) =>
                  dispatch(actions.setListLayoutMode({ context: PieceListContext.MINE, mode }))
                }
              />
            </SidebarSection>
          </BuilderSidebar>

          <section className="flex-col-el relative flex-1 h-full p-4">
            <div
              ref={scrollCallbackRef}
              onScroll={onScroll}
              className="flex-col-el flex-1 w-full overflow-y-auto invisible-scrollbar"
            >
              <div className={`${LAYOUT_GRID_CLASS[layoutMode]} w-full pb-4`}>
                <BuilderTutorialCard layoutMode={layoutMode} onClick={() => setShowGuide(true)} />
                {visibleMaps.map((m, index) =>
                  layoutMode === PieceLayoutMode.LIST ? (
                    <MapListItem
                      key={m.piece_id}
                      id={index === 0 ? GuideTargetId.LIST_ITEM : undefined}
                      piece={m}
                      context={PieceListContext.MINE}
                      onAction={handleAction(m)}
                    />
                  ) : (
                    <MapCard
                      key={m.piece_id}
                      id={index === 0 ? GuideTargetId.LIST_ITEM : undefined}
                      piece={m}
                      context={PieceListContext.MINE}
                      onAction={handleAction(m)}
                      compact={layoutMode === PieceLayoutMode.GRID_2}
                      heroPlay={layoutMode === PieceLayoutMode.GRID_1}
                    />
                  ),
                )}
                <AddMapCard ref={addCardRef} layoutMode={layoutMode} onClick={handleOpenCreate} />
              </div>
            </div>

            {!addCardVisible && (
              <AddMapButton
                onClick={() =>
                  scrollRef.current?.scrollTo({
                    top: scrollRef.current.scrollHeight,
                    behavior: "smooth",
                  })
                }
              />
            )}
          </section>

          <CreateMapModal
            visible={createOpen}
            defaultName={I18n.t("MSG_BUILDER_NEW_PIECE_NAME")}
            close={() => btnDelay(() => setCreateOpen(false))}
            onCreate={handleCreate}
          />
        </div>
      )}

      {/* アクション確認Modal（早期returnの外＝一覧・詳細どちらでも描画） */}
      <BuilderConfirmDialog
        visible={trashTarget != null}
        icon="delete"
        confirmIcon="delete"
        title={I18n.t("MSG_BUILDER_TO_TRASH_CONFIRM_TITLE")}
        message={
          trashTarget ? I18n.t("MSG_BUILDER_TO_TRASH_CONFIRM_MSG", { name: trashTarget.name }) : ""
        }
        close={() => setTrashTarget(null)}
        handleConfirm={handleConfirmTrash}
      />

      {/* コピー確認 */}
      <BuilderConfirmDialog
        visible={copyTarget != null}
        accent={BuilderModalAccent.BLUE}
        icon="content_copy"
        confirmIcon="content_copy"
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        confirmSound={`${Constants.assetHost}/assets/blockly/start.mp3`}
        title={I18n.t("MSG_BUILDER_MENU_COPY")}
        message={copyTarget ? `「${copyTarget.name}」` : ""}
        close={() => setCopyTarget(null)}
        handleConfirm={handleConfirmCopy}
      />

      {/* 公開/非公開 確認 */}
      {/* ゲストは公開できない。押した時点で理由と次の一手を出す */}
      <BuilderAccountRequiredDialog
        visible={showCreateDenied}
        accent={BuilderModalAccent.BLUE}
        icon="extension"
        title={I18n.t("MSG_BUILDER_CREATE_DENIED_TITLE")}
        message={I18n.t("MSG_BUILDER_CREATE_DENIED_MESSAGE")}
        close={() => setShowCreateDenied(false)}
      />

      <BuilderAccountRequiredDialog
        visible={showPublishDenied}
        accent={BuilderModalAccent.GREEN}
        icon="public"
        title={I18n.t("MSG_BUILDER_PUBLISH_DENIED_TITLE")}
        message={I18n.t("MSG_BUILDER_PUBLISH_DENIED_MESSAGE")}
        close={() => setShowPublishDenied(false)}
      />

      {/* 運営が公開を止めている。保護者ページでは直せないので、閉じるだけにする */}
      <BuilderInfoDialog
        visible={showPublishRestricted}
        accent={BuilderModalAccent.GREEN}
        icon="public_off"
        title={I18n.t("MSG_USER_RESTRICTION_PIECE_PUBLISH_TITLE")}
        message={I18n.t("MSG_USER_RESTRICTION_PIECE_PUBLISH_MESSAGE", {
          until: restrictionUntilText(restriction),
        })}
        close={() => setShowPublishRestricted(false)}
      />

      <BuilderConfirmDialog
        extra={
          publishTarget && !publishTarget.published ? (
            <PiecePublishOptions
              copyable={publishOptions.copyable}
              commentable={publishOptions.commentable}
              onChange={setPublishOptions}
            />
          ) : undefined
        }
        visible={publishTarget != null}
        accent={publishTarget?.published ? BuilderModalAccent.BROWN : BuilderModalAccent.GREEN}
        icon={publishTarget?.published ? "lock" : "public"}
        confirmIcon={publishTarget?.published ? "lock" : "public"}
        confirmLabel={I18n.t("MSG_CONFIRM_BTN")}
        confirmSound={`${Constants.assetHost}/assets/sounds/pop-up-off.mp3`}
        title={I18n.t(
          publishTarget?.published
            ? "MSG_BUILDER_CARD_STATUS_UNPUBLISHED"
            : "MSG_BUILDER_PUBLISH_SHARE_BTN",
        )}
        message={I18n.t(
          publishTarget?.published ? "MSG_BUILDER_UNPUBLISH_HINT" : "MSG_BUILDER_PUBLISH_HINT",
        )}
        close={() => setPublishTarget(null)}
        handleConfirm={handleConfirmPublish}
      />

      {/* ヒント（情報） */}
      <BuilderInfoDialog
        visible={hintTarget != null}
        accent={BuilderModalAccent.BLUE}
        icon="lightbulb"
        title={I18n.t("MSG_BUILDER_DETAIL_HINT_TITLE")}
        message={I18n.t("MSG_BUILDER_MENU_HINT")}
        close={() => setHintTarget(null)}
      />

      <BuilderGuideBook
        open={showGuide}
        book={GuideBookId.TOP}
        chapters={TOP_GUIDE_CHAPTERS}
        onClose={() => setShowGuide(false)}
      />
    </>
  );
};
